import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import { addMonths } from 'lib/datetime/dateUtils'
import AIGlowHighlightContext from 'contexts/aiGlowHighlightContext'
import { type CalendarFocusMonth, resolveVerticalCalendarFocusDate } from './calendarFocusMonth'
import CalendarRangeGlowOverlay from './CalendarRangeGlowOverlay'
import VerticalDatePickerHeader from './VerticalDatePickerHeader'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import moment, { type Moment } from 'moment'
import SearchDatePickerDay from 'components/SearchV2/Components/Mobile/DateSearchFloatingPanel/SearchDatePickerDay'
import SearchDatePickerCalendar from 'components/SearchV2/Components/Mobile/DateSearchFloatingPanel/SearchDatePickerCalendar'
import { useInView } from 'react-intersection-observer'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'

const INITIAL_MONTHS = 3
const LOAD_MORE_MONTHS = 2

const MonthsContainer = styled(VerticalSpacer)`
  button:disabled {
    opacity: 0.4;
  }
`

function monthOrdinal(date: Date): number {
  return date.getFullYear() * 12 + date.getMonth()
}

interface Props {
  startDate?: Moment
  endDate?: Moment | null
  min: Date
  max: Date
  onDatesChange?: (dates: { startDate: Moment; endDate?: Moment }) => void
  allowSameDayRange?: boolean
  className?: string
  contentRef?: React.RefObject<HTMLElement | null>
  availableMonths?: Array<string>
  focusMonth?: CalendarFocusMonth
}

function VerticalDateRangePicker(props: Props) {
  const { startDate, endDate, min, max, onDatesChange, allowSameDayRange, className, contentRef, availableMonths, focusMonth } = props
  const isAIGlowHighlighted = useContext(AIGlowHighlightContext)

  const onDayClick = useCallback((selectedDay: Date) => {
    const day = moment(selectedDay)
    const isSameStartDay = startDate && day.isSame(startDate, 'day')
    const shouldSelectEndDate = startDate && !endDate && (day.isAfter(startDate) || (allowSameDayRange && isSameStartDay))

    if (shouldSelectEndDate) {
      onDatesChange?.({ startDate, endDate: day })
    } else {
      onDatesChange?.({ startDate: day, endDate: undefined })
    }
  }, [allowSameDayRange, endDate, onDatesChange, startDate])

  const period = useMemo(() => {
    if (availableMonths?.length) {
      return availableMonths.map(availableMonth => moment(availableMonth, 'YYYY-MM').toDate())
    }

    const monthsDiffs = moment(max).diff(min, 'months')
    const dates: Array<Date> = [min]
    for (let i = 0; i < monthsDiffs; i++) {
      dates.push(addMonths(dates[i], 1))
    }
    return dates
  }, [availableMonths, min, max])

  const selectedStartTime = startDate?.valueOf()
  const focusIndex = useMemo(() => {
    const selectedStart = selectedStartTime !== undefined ? new Date(selectedStartTime) : undefined
    const focusDate = resolveVerticalCalendarFocusDate(focusMonth, selectedStart)
    if (!focusDate || !period.length) return 0

    const target = monthOrdinal(focusDate)
    const index = period.findIndex(month => monthOrdinal(month) >= target)
    return index === -1 ? period.length - 1 : index
  }, [focusMonth, period, selectedStartTime])

  const [monthsToShow, setMonthsToShow] = useState(() => Math.min(focusIndex + INITIAL_MONTHS, period.length))

  const [sentinelRef, sentinelInView] = useInView({
    root: contentRef?.current ?? undefined,
    rootMargin: '0px 0px 100px 0px',
  })

  useEffect(() => {
    if (sentinelInView) {
      setMonthsToShow(prev => Math.min(prev + LOAD_MORE_MONTHS, period.length))
    }
  }, [sentinelInView, period.length])

  // The focus month can arrive after mount, once trip data has loaded.
  useEffect(() => {
    setMonthsToShow(prev => Math.max(prev, Math.min(focusIndex + INITIAL_MONTHS, period.length)))
  }, [focusIndex, period.length])

  const focusMonthRef = useRef<HTMLDivElement>(null)
  const lastScrolledFocusIndexRef = useRef<number | undefined>(undefined)

  useEffect(() => {
    // No focus (or focus is already the first month): stay at the top, and
    // clear the last-scrolled index so a later restore of the same month can
    // re-scroll (e.g. after the traveller clears a picked date).
    if (focusIndex === 0) {
      lastScrolledFocusIndexRef.current = undefined
      return
    }

    if (lastScrolledFocusIndexRef.current === focusIndex) return

    const focusElement = focusMonthRef.current
    if (!focusElement) return

    lastScrolledFocusIndexRef.current = focusIndex
    const scroller = contentRef?.current
    if (scroller) {
      scroller.scrollTop += focusElement.getBoundingClientRect().top - scroller.getBoundingClientRect().top
    } else {
      focusElement.scrollIntoView({ block: 'start' })
    }
  }, [contentRef, focusIndex, monthsToShow])

  // Pre-computed timestamps for O(1) comparisons — avoids creating a moment()
  // object for every day on every re-render
  const startTs = startDate?.valueOf() ?? -1
  const endTs = endDate?.valueOf() ?? -1
  const minTs = min.getTime()
  const maxTs = max.getTime()
  const isRangeGlowing = isAIGlowHighlighted && startTs !== -1 && endTs !== -1

  const dayRender = useCallback((day: Date) => {
    const dayTime = day.getTime()
    const selected = dayTime === startTs || dayTime === endTs
    const disabled = dayTime < minTs || dayTime > maxTs
    const isInRange = !selected && startTs !== -1 && endTs !== -1
      ? dayTime > startTs && dayTime < endTs
      : false
    const isSingleDayRange = selected && startTs !== -1 && endTs !== -1 && startTs === endTs

    return (
      <SearchDatePickerDay
        key={dayTime}
        day={day}
        data-testid={`day-${moment(day).format(ISO_DATE_FORMAT)}`}
        selected={selected}
        disabled={disabled}
        isInRange={isInRange}
        isSingleDayRange={isSingleDayRange}
        suppressRangeHighlight={isRangeGlowing}
        onClick={onDayClick}
      />
    )
  }, [startTs, endTs, minTs, maxTs, onDayClick, isRangeGlowing])

  const visiblePeriod = period.slice(0, monthsToShow)

  // Stable identities so the overlay can memoize its segment maths across re-renders.
  const glowingRange = useMemo(
    () => isRangeGlowing ? { start: new Date(startTs), end: new Date(endTs) } : undefined,
    [isRangeGlowing, startTs, endTs],
  )

  return (
    <MonthsContainer gap={32} className={className}>
      {visiblePeriod.map((d, index) => {
        const rangeGlowBand = glowingRange ? (
          <CalendarRangeGlowOverlay
            monthStart={d}
            rangeStart={glowingRange.start}
            rangeEnd={glowingRange.end}
          />
        ) : undefined

        return (
          <VerticalSpacer gap={16} key={d.toString()} ref={index === focusIndex ? focusMonthRef : undefined}>
            <VerticalDatePickerHeader currentDate={d} />
            <SearchDatePickerCalendar
              month={d.getMonth()}
              year={d.getFullYear()}
              dayRender={dayRender}
              fixedHeight={false}
              gridOverlay={rangeGlowBand}
            />
          </VerticalSpacer>
        )
      })}
      {monthsToShow < period.length && <div ref={sentinelRef} />}
    </MonthsContainer>
  )
}

export default React.memo(VerticalDateRangePicker)
